import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FaLock, FaEye, FaEyeSlash, FaKey } from "react-icons/fa";
import { toast } from "react-toastify";
import API from "../../services/api";

const PasswordChangeModal = ({ show, onHide, onSuccess, userId }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 6) errors.push("At least 6 characters");
    if (!/[0-9]/.test(password)) errors.push("At least one number");
    if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate current password
    if (!currentPassword) {
      setErrors({ current: "Current password is required" });
      return;
    }

    // Validate new password
    if (!newPassword) {
      setErrors({ new: "New password is required" });
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setErrors({ passwordStrength: passwordErrors });
      return;
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      setErrors({ confirm: "Passwords do not match" });
      return;
    }

    try {
      setLoading(true);

      // Correct API endpoint with student ID
      const response = await API.post(`/student/${userId}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data.status) {
        // Store the timestamp of password change in localStorage
        const now = Date.now();
        localStorage.setItem("lastPasswordChange", now.toString());
        localStorage.setItem("passwordChangeReminder", "true");

        toast.success("Password changed successfully!");
        onSuccess();
        onHide();

        // Reset form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      console.error("Password change error:", error);
      const errorMsg =
        error.response?.data?.message || "Failed to change password";
      if (errorMsg.toLowerCase().includes("current password")) {
        setErrors({ current: errorMsg });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const errors = validatePassword(newPassword);
    if (!newPassword) return { level: 0, text: "" };
    if (errors.length === 0)
      return { level: 3, text: "Strong", color: "#28a745" };
    if (errors.length <= 1)
      return { level: 2, text: "Medium", color: "#ffc107" };
    return { level: 1, text: "Weak", color: "#dc3545" };
  };

  const strength = getPasswordStrength();

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton={false} className="bg-primary text-white">
        <Modal.Title>
          <FaKey className="me-2" />
          Change Your Password
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <FaLock className="me-2" />
          <strong>Security Reminder:</strong> For your account security, please
          change your password. You will be reminded to change your password
          every 365 days.
        </Alert>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>Current Password *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                isInvalid={!!errors.current}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.current}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>New Password *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                isInvalid={!!errors.new}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {newPassword && (
              <div className="mt-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small>Password Strength:</small>
                  <small style={{ color: strength.color }}>
                    {strength.text}
                  </small>
                </div>
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${(strength.level / 3) * 100}%`,
                      backgroundColor: strength.color,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}

            <Form.Text className="text-muted">
              Password must be at least 6 characters, contain at least one
              number and one uppercase letter.
            </Form.Text>

            {errors.passwordStrength && (
              <div className="mt-2">
                <small className="text-danger">
                  <strong>Requirements:</strong>
                  <ul className="mb-0 mt-1">
                    {errors.passwordStrength.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </small>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm New Password *</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                isInvalid={!!errors.confirm}
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.confirm}
            </Form.Control.Feedback>
          </Form.Group>

          <Alert variant="warning" className="mb-0">
            <small>
              <strong>Why do I need to change my password?</strong>
              <br />
              For security reasons, we recommend changing your password every
              365 days to keep your account safe.
            </small>
          </Alert>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            loading || !currentPassword || !newPassword || !confirmPassword
          }
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : (
            <>
              <FaKey className="me-2" />
              Update Password
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PasswordChangeModal;
